import { useState, useEffect, useRef } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
} from "recharts";
import DashboardMetricsService from "../../services/dashboardMetricsService";
import { buildAnalyticsQueryParams } from "../../helpers/helpers";
import {
    UsersIcon,
    UserGroupIcon,
    ChatBubbleBottomCenterTextIcon,
    ChatBubbleLeftRightIcon,
    CubeIcon,
    CurrencyDollarIcon,
    ArrowTopRightOnSquareIcon,
    ArrowUpRightIcon,
    ArrowDownLeftIcon,
    CircleStackIcon,
    PhotoIcon,
} from "@heroicons/react/24/outline";
import { compactFmt, public_labels, chartTheme } from "../../components/ChartComponents";
import { createFormatters } from "../../helpers/dateFormatter";
import { DateTime } from "luxon";
import { useDashboardUI } from "../../context/GlobalContext";

const GenericAnalytics = ({ tokenUsage }) => {

    const adminEmail = localStorage.getItem("admin_email");
    if (!adminEmail) {
        return;
    }

    const [startDate, setStartDate] = useState(() => {
        return DateTime.local().minus({ days: 7 }).startOf("day").toJSDate();
    });
    const [endDate, setEndDate] = useState(() => {
        return DateTime.local().endOf("day").toJSDate();
    });
    const [userStats, setUserStats] = useState({ count: null });
    const [chatStats, setChatStats] = useState({ count: null, totalCount: null });
    const [queryStats, setQueryStats] = useState({ count: null, totalCount: null });
    const tokenUsageSummary = {
        used: tokenUsage?.usage_summary?.used ?? "0",
        allocated: tokenUsage?.usage_summary?.allocated ?? "0",
    };
    const [userCountOverTime, setUserCountOverTime] = useState([]);
    const [activeUserOverTime, setActiveUserOverTime] = useState([]);
    const [conversationOverTime, setConversationOverTime] = useState([]);
    const [queriesOverTime, setQueriesOverTime] = useState([]);
    const [tokenUsageOverTime, setTokenUsageOverTime] = useState([]);
    const [expandedStat, setExpandedStat] = useState(null);
    const [statCols, setStatCols] = useState(1);
    const [shouldSwitchChartOrder, setShouldSwitchChartOrder] = useState(false);
    const statsRef = useRef(null);
    const panelRef = useRef(null);
    const {
        selectedFacilityFilter,
        totalActiveSessions,
        fileGenSummary,
    } = useDashboardUI();

    const chartDataMap = {
        "Total Users": userCountOverTime,
        "Active Users": activeUserOverTime,
        "Total Chats": conversationOverTime,
        "Total Tokens": tokenUsageOverTime,
        "Total Queries": queriesOverTime,
        "Total Active Session": totalActiveSessions
    };
    const dataKeyMap = {
        "Total Users": "user_count",
        "Active Users": "active_user_count",
        "Total Chats": "conversation_count",
        "Total Tokens": "tokens",
        "Total Queries": "conversation_count",
        "Total Active Session": totalActiveSessions
    };

    const fmtPct = new Intl.NumberFormat("en", {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
    }).format;

    const statList = [
        {
            title: "Total Users",
            value: userStats.count,
            icon: UserGroupIcon,
            chartName: "User Growth - Past 6M",
        },
        {
            title: "Total Active Session",
            icon: UsersIcon,
            value: totalActiveSessions,
            chartName: "Active Session - Past 7D"
        },
        {
            title: "Total Token Consumed (Overall)",
            value: `${tokenUsageSummary.used}/${tokenUsageSummary.allocated}`,
            icon: CircleStackIcon,
        },
        {
            title: "Total Chats",
            value: chatStats.count,
            icon: ChatBubbleBottomCenterTextIcon,
            chartName: "Chats - Past 6M",
            totalCount: chatStats?.totalCount
        },
        {
            title: "Total Queries",
            value: queryStats.count,
            icon: ChatBubbleLeftRightIcon,
            chartName: "Queries - Past 6M",
            totalCount: queryStats?.totalCount
        },
        {
            title: "Total Images Generated (Today)",
            value: `${fileGenSummary.totalImages}/${fileGenSummary.maxImagesPerDay}`,
            icon: PhotoIcon,
        },
    ];

    const fetchKpiSummary = async () => {
        try {
            const queryParams = buildAnalyticsQueryParams({
                startValue: startDate,
                endValue: endDate,
                facilityId: selectedFacilityFilter?.facility_number,
            });

            const res = await DashboardMetricsService.getKpiSummary(queryParams);
            const data = res.data || {};

            setUserStats({ count: data.total_users?.stat ?? null });
            setChatStats({
                count: data.total_chats?.stat ?? null,
                totalCount: data.total_chats?.total_count ?? null,
            });
            setQueryStats({
                count: data.total_queries?.stat ?? null,
                totalCount: data.total_queries?.total_count ?? null,
            });
        } catch (error) {
            console.error("Error fetching KPI summary", error);
        }
    };

    useEffect(() => {
        const handler = (event) => {
            const { start: s, end: e } = event.detail || {};
            setStartDate(s || null);
            setEndDate(e || null);
        };
        window.addEventListener("dateRangeChanged", handler);
        return () => window.removeEventListener("dateRangeChanged", handler);
    }, []);

    useEffect(() => {
        if (startDate && endDate) {
            fetchKpiSummary();
        }
    }, [startDate, endDate, selectedFacilityFilter]);

    useEffect(() => {
        const gap = 16;
        const minW = 292;
        const calculateCols = () => {
            const w = window.innerWidth;
            let cols;
            if (w >= (minW * 3 + gap * 2)) cols = 3;
            else cols = 2;
            setStatCols(cols);
        };
        calculateCols();
        window.addEventListener("resize", calculateCols);
        return () => window.removeEventListener("resize", calculateCols);
    }, []);

    useEffect(() => {
        const gap = 16;
        const minW = 360;
        const calcShouldSwitch = () => {
            const w = window.innerWidth;
            setShouldSwitchChartOrder(w < minW * 3 + gap * 2);
        };
        calcShouldSwitch();
        window.addEventListener("resize", calcShouldSwitch);
        return () => window.removeEventListener("resize", calcShouldSwitch);
    }, []);

    useEffect(() => {
        function handleClickOutside(e) {
            if (
                expandedStat &&
                panelRef.current &&
                !panelRef.current.contains(e.target)
            ) {
                setExpandedStat(null);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [expandedStat]);

    return (
        <div className="py-4">
            {/* Stats */}
            {/* Phones: 1 column, tablets: 2, laptops and up: the original repeat(statCols, minmax(225px,1fr)) */}
            <div ref={statsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:[grid-template-columns:repeat(var(--stat-cols),minmax(225px,1fr))] gap-4 mb-4 overflow-visible"
                style={{ "--stat-cols": statCols }} >

                {statList.map(
                    ({
                        title,
                        value,
                        icon: Icon,
                        change,
                        isIncrease,
                        chartName,
                        totalCount,
                    }) => {
                        const negative = !isIncrease;
                        const ArrowComp = negative
                            ? ArrowDownLeftIcon
                            : ArrowUpRightIcon;
                        const colorClass = negative
                            ? "text-red-500"
                            : "text-green-500";
                        const xKey = title === "Total Tokens" ? "date" : "month";
                        const raw = chartDataMap[title] || [];
                        const windowSize = raw.length
                        const { tickFormatter, labelFormatter } = createFormatters(xKey, windowSize);

                        // Presentation only: "Total Tokens Consumed (Overall)" → light "(Overall)",
                        // "353.3M/5B" → big "353.3M" + light "/ 5B".
                        const titleParts = String(title).match(/^(.*?)(\s\([^)]*\))$/);
                        const titleMain = titleParts ? titleParts[1] : title;
                        const titleNote = titleParts ? titleParts[2] : "";
                        const valueText = value ?? "–";
                        const slashAt = typeof valueText === "string" ? valueText.indexOf("/") : -1;
                        const valueMain = slashAt > 0 ? valueText.slice(0, slashAt) : valueText;
                        const valueSuffix = slashAt > 0 ? valueText.slice(slashAt + 1) : "";

                        return (
                            <div key={title} className="relative">
                                {/* stat card */}
                                <div
                                    className={`bg-white px-5 sm:px-6 py-5 min-w-0 sm:min-w-[225px] shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]
                                ${expandedStat === title
                                            ? "rounded-t-2xl rounded-b-none"
                                            : "rounded-2xl"
                                        }`}
                                >
                                    {/* {chartName && (
                                        <button
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={() =>
                                                setExpandedStat(
                                                    expandedStat === title
                                                        ? null
                                                        : title
                                                )
                                            }
                                            className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600"
                                        >
                                            <ArrowTopRightOnSquareIcon
                                                className={`w-5 h-5 transition-transform ${expandedStat === title
                                                    ? "rotate-180"
                                                    : ""
                                                    }`}
                                            />
                                        </button>
                                    )} */}
                                    <div className="flex min-w-0 items-center gap-3">
                                        <div className="w-11 h-11 shrink-0 flex items-center justify-center bg-[#F3EAFE] rounded-xl">
                                            <Icon className="w-5 h-5 text-[#7C3AED]" />
                                        </div>
                                        <div className="flex min-w-0 flex-1 flex-col gap-2">
                                            <p className="min-w-0 line-clamp-2 break-words text-sm sm:text-[15px] leading-snug font-medium text-[#4B5266]" title={title}>
                                                {titleMain}
                                                {titleNote && <span className="font-normal text-[#9AA1B2]">{titleNote}</span>}
                                            </p>
                                        <div className="min-w-0">
                                            <p className="truncate text-2xl sm:text-3xl font-extrabold leading-none tracking-tight text-[#14122B]" {...(totalCount != null ? { title: totalCount } : {})} >
                                                {valueMain}
                                                {valueSuffix && (
                                                    <span className="ml-1 text-base font-medium tracking-normal text-[#9AA1B2]">/ {valueSuffix}</span>
                                                )}
                                            </p>
                                            {change != null && (
                                                <div className="mt-2 flex items-center text-sm">
                                                    <ArrowComp
                                                        className={`w-4 h-4 ${colorClass} mr-1`}
                                                    />
                                                    <span className="text-gray-400">
                                                        {fmtPct(Number(change))}%
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        </div>
                                    </div>
                                </div>

                                {/* dropdown chart under the card */}
                                {expandedStat === title && (
                                    <div
                                        ref={panelRef}
                                        className="absolute left-0 top-full w-full bg-white rounded-b-xl shadow-lg p-2 z-50"
                                    >
                                        <div className="border-t border-gray-300 w-11/12 mx-auto mb-2" />
                                        <h2 className="text-gray-800 pl-4 pb-4">
                                            {chartName}
                                        </h2>
                                        <ResponsiveContainer
                                            width="100%"
                                            height={180}
                                        >
                                            <LineChart
                                                data={chartDataMap[title] || []}
                                                margin={{
                                                    top: chartTheme.margins.top,
                                                    right: chartTheme.margins
                                                        .right,
                                                    bottom: chartTheme.margins
                                                        .bottom,
                                                    left: 0,
                                                }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.colors.grid} vertical={false} />
                                                <defs>
                                                    <linearGradient
                                                        id="dropGrad"
                                                        x1="0"
                                                        y1="0"
                                                        x2="0"
                                                        y2="1"
                                                    >
                                                        <stop
                                                            offset="0%"
                                                            stopColor={
                                                                chartTheme
                                                                    .colors
                                                                    .primary
                                                            }
                                                            stopOpacity={0.8}
                                                        />
                                                        <stop
                                                            offset="100%"
                                                            stopColor={
                                                                chartTheme
                                                                    .colors
                                                                    .primary
                                                            }
                                                            stopOpacity={0.1}
                                                        />
                                                    </linearGradient>
                                                </defs>
                                                <XAxis
                                                    dataKey={title === "Total Tokens" ? "date" : "month"}
                                                    tickMargin={12}
                                                    tickLine={false}
                                                    axisLine={false}
                                                    tickFormatter={tickFormatter}
                                                />
                                                <YAxis tickMargin={12} tickLine={false} axisLine={false} domain={[0, dataMax => Math.ceil(dataMax * 1.1)]} tickFormatter={val => compactFmt(val)} />
                                                <Tooltip
                                                    wrapperStyle={{
                                                        backgroundColor:
                                                            chartTheme.colors
                                                                .tooltipBg,
                                                        boxShadow:
                                                            chartTheme.colors
                                                                .tooltipShadow,
                                                        borderRadius: 8,
                                                    }}
                                                    formatter={(value, name) => {
                                                        const label = public_labels[name] ?? name;
                                                        return [value, label];
                                                    }}
                                                    labelFormatter={labelFormatter}
                                                />
                                                <Line
                                                    className="shadowed-line"
                                                    type="monotone"
                                                    dataKey={dataKeyMap[title]}
                                                    stroke={
                                                        chartTheme.colors
                                                            .primary
                                                    }
                                                    strokeWidth={3}
                                                    dot={false}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </div>
                        );
                    }
                )}
            </div>

        </div>
    );
};

export default GenericAnalytics;
